import type { Editor as TiptapEditor } from '@tiptap/react';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type RefObject,
  type KeyboardEvent,
} from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import { dropdownContentClasses } from 'src/components/buttons-and-actions/dropdown-trigger';
import { useFloating } from 'src/hooks/use-floating';
import { cn } from 'src/utils/cn';

type HeadingPopoverProps = {
  editor: TiptapEditor;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  triggerRef?: RefObject<HTMLElement>;
};

export const HeadingPopover = ({
  editor,
  isActive,
  setIsActive,
  triggerRef,
}: HeadingPopoverProps) => {
  const { refs, style } = useFloating({
    placement: 'bottom',
  });
  const headingHandler = useHeadingHandler({
    editor,
    onSetHeading: () => setIsActive(false),
  });
  const popoverRef = useRef<HTMLDivElement>(null);
  const firstHeadingButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (triggerRef?.current) {
      refs.setReference(triggerRef.current);
    }
  }, [triggerRef, refs]);

  useEffect(() => {
    if (isActive && firstHeadingButtonRef.current) {
      firstHeadingButtonRef.current.focus();
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsidePopover = popoverRef.current?.contains(target);
      const isClickOnTrigger = triggerRef?.current?.contains(target);

      if (!isClickInsidePopover && !isClickOnTrigger) {
        setIsActive(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, setIsActive, triggerRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsActive(false);
      } else if (e.key === 'Tab') {
        // Use setTimeout to check if focus moved outside the popover
        setTimeout(() => {
          if (!popoverRef.current?.contains(document.activeElement)) {
            setIsActive(false);
          }
        }, 1);
      }
    },
    [setIsActive]
  );

  if (!isActive) return null;

  return (
    <div ref={popoverRef} className="relative">
      <div
        ref={refs.setFloating}
        style={style}
        className={cn(
          // Since we are not using our own dropdown, we call here the styles from DropdownTrigger to mimic the styles.
          // This poses that items within should be rendered as `<Button mode="dropdown-item" />`
          dropdownContentClasses,
          `
            min-w-auto
            not-legacy:grid not-legacy:gap-xs not-legacy:rounded-xs
            not-legacy:px-xs not-legacy:py-sm
          `
        )}
      >
        <HeadingContent
          currentLevel={headingHandler.currentLevel}
          setHeading={headingHandler.setHeading}
          onKeyDown={handleKeyDown}
          firstHeadingButtonRef={firstHeadingButtonRef}
        />
      </div>
    </div>
  );
};

type HeadingContentProps = {
  currentLevel: number | null;
  setHeading: (level: number) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  firstHeadingButtonRef: RefObject<HTMLButtonElement>;
};

function HeadingContent({
  currentLevel,
  setHeading,
  onKeyDown,
  firstHeadingButtonRef,
}: HeadingContentProps) {
  const headingOptions = [
    { level: 1, label: 'Heading 1' },
    { level: 2, label: 'Heading 2' },
    { level: 3, label: 'Heading 3' },
    { level: 4, label: 'Heading 4' },
    { level: 5, label: 'Heading 5' },
    { level: 6, label: 'Heading 6' },
  ] as const;

  return (
    <>
      {headingOptions.map((option, index) => {
        const isActive = currentLevel === option.level;

        return (
          <div
            key={option.level}
            className={cn(`
              legacy:border-b-[0.1rem] legacy:border-b-legacy-brand-light-3
              legacy:py-[0.9rem]
              legacy:last:border-b-0
            `)}
          >
            <Button
              mode="dropdown-item"
              onClick={() => setHeading(option.level)}
              onKeyDown={onKeyDown}
              aria-label={option.label}
              aria-pressed={isActive}
              ref={index === 0 ? firstHeadingButtonRef : undefined}
            >
              {option.label}
            </Button>
          </div>
        );
      })}
    </>
  );
}

type HeadingHandlerProps = {
  editor: TiptapEditor;
  onSetHeading?: () => void;
};

function useHeadingHandler({ editor, onSetHeading }: HeadingHandlerProps) {
  const [currentLevel, setCurrentLevel] = useState<number | null>(null);

  useEffect(() => {
    if (isHeadingActive(editor)) {
      const { level } = editor.getAttributes('heading');
      setCurrentLevel(level || null);
    } else {
      setCurrentLevel(null);
    }
  }, [editor]);

  useEffect(() => {
    const updateHeadingState = () => {
      if (isHeadingActive(editor)) {
        const { level } = editor.getAttributes('heading');
        setCurrentLevel(level || null);
      } else {
        setCurrentLevel(null);
      }
    };

    editor.on('selectionUpdate', updateHeadingState);
    return () => {
      editor.off('selectionUpdate', updateHeadingState);
    };
  }, [editor]);

  const setHeading = useCallback(
    (level: number) => {
      editor
        .chain()
        .focus()
        .toggleNode('heading', 'paragraph', { level })
        .run();

      if (editor.isActive('heading')) {
        const { level: newLevel } = editor.getAttributes('heading');
        setCurrentLevel(newLevel || null);
      } else {
        setCurrentLevel(null);
      }

      onSetHeading?.();
    },
    [editor, onSetHeading]
  );

  return {
    currentLevel,
    setHeading,
    isActive: isHeadingActive(editor),
  };
}

function isHeadingActive(editor: TiptapEditor): boolean {
  if (!editor.isEditable) return false;
  return editor.isActive('heading');
}
